import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  Select,
  FormControl,
  MenuItem,
  Input,
  // IconButton,
  Tooltip,
  useMediaQuery,
  Avatar,
  Grid,
  Container,
  Typography,
  InputLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { updateYourLocation } from '../../../actions/mainLayout';
import { triggerClearCache } from '../../../actions/misc/clearCache';
import { postIframe, formatImageUrlFromStrapi, keySort } from '../../../utils/functions';
import {
  defaultIntlId,
  defaultAlpha2,
  manuallyChosenAlpha2Storage,
  userSettingsExpireTimeHrs,
  templateStorage,
  dutchTemplate,
} from '../../../utils/variables';
import { scrollStyles } from '../../../styles';
import messages from './message';
import styles from './style';
import CountryChecker from './countryChecker';
import { countryVerificationStorage } from '../../../storage';
import { useSmall, useRouter } from '../../../hooks';
import routes from '../../../utils/variables/routes';
import Cookies from '../../../cookies/cookies';

const displayFlag = 'flag';
const displayFlagButton = 'flagButton';
const displayFullText = 'fullText';

const useStyles = makeStyles(styles);
const useScrollStyles = makeStyles(scrollStyles);

function Location({
  view,
  reverseColor,
  backgroundColorOfImage,
  onCustomClick,
  ...props
}) {
  const classes = useStyles();
  const scrollClasses = useScrollStyles();
  const dispatch = useDispatch();
  const router = useRouter();
  const intl = useIntl();
  const theme = useTheme();
  const countrySelectorRef = useRef();
  const initialAllLocations = useSelector(state => state.locations.allLocations) || [];
  const isAgentGroupAdmin = useSelector(s => !!s?.auth?.user?.agent?.isGroupAdmin);
  const agentTeritory = useSelector(s => s?.auth?.user?.agent?.territory);
  let allLocations = initialAllLocations?.filter(x => !x.hideInDropdown);
  allLocations = keySort(allLocations, 'positionNo');
  const key = 'alpha2';
  allLocations = [...new Map(allLocations?.map(item => [item[key], item])).values()];
  const isDutch = theme?.template === dutchTemplate;
  // // console.log('allLocations: ', allLocations);

  let yourLocation = useSelector(state => state.locations.yourLocation);
  // yourLocation = isAgentGroupAdmin ? allLocations?.filter(x => x?.alpha2 === (agentTeritory === 'SXM' ? 'SX' : 'CW'))?.[0] : yourLocation;
  // // console.log('yourLocation: ', yourLocation);
  const isSmall = useSmall();

  const tempLocation = initialAllLocations?.find(x => x?.alpha2 === yourLocation?.alpha2);
  const countryCode = tempLocation?.alpha2 || defaultAlpha2;
  // // console.log('countryCode: ', countryCode);
  yourLocation = initialAllLocations?.find(x => x?.alpha2 === countryCode);

  // Restrict available locations for agent group admins based on their current location
  if (isAgentGroupAdmin) {
    // If current location is Curaçao (CW), show only specific Caribbean locations
    if (agentTeritory === 'CUW') {
      allLocations = allLocations.filter(
        x =>
          x.alpha2 === 'CW' ||
          x.alpha2 === 'SX' ||
          x.alpha2 === 'BQ-SA' ||
          x.alpha2 === 'BQ-SE' ||
          x.alpha2 === 'BQ-BO' ||
          x.alpha2 === 'AW',
      );
      // If current location is Sint Maarten (SX), show only SX and Bonaire subregions
    } else if (agentTeritory === 'SXM') {
      allLocations = allLocations.filter(
        x => x.alpha2 === 'SX' || x.alpha2 === 'BQ-SA' || x.alpha2 === 'BQ-SE',
      );
    }
  }

  const sendDataToIframe = location => {
    postIframe({
      yourLocation: location,
    });
  };

  const runClearCache = clearCacheKey => {
    const clearCacheKeys = clearCacheKey?.split(',') || [];
    for (let i = 0; i < clearCacheKeys?.length; i++) {
      // await promiseTimeout(300, null);
      const key = clearCacheKeys[i];
      dispatch(triggerClearCache(key));
    }
  };

  const handleChange = newAlpha2 => {
    // // console.log('event: ', event);
    const newCountryValue = newAlpha2;
    if (onCustomClick) onCustomClick('country-switcher');
    // // console.log('newCountryValue: ', newCountryValue);
    // dispatch(newCountryValue);
    const newLocation = allLocations?.find(x => x.alpha2 === newCountryValue);
    const storedManuallyChosenAlpha2 = Cookies.getCookie(manuallyChosenAlpha2Storage);
    if (!storedManuallyChosenAlpha2) {
      Cookies.setCookie(
        manuallyChosenAlpha2Storage,
        newCountryValue,
        userSettingsExpireTimeHrs,
      );
    }
    // remove any url params in url
    // window.history.replaceState(null, '', window.location.pathname);
    dispatch(updateYourLocation.updateYourLocationAction(newLocation));
    sendDataToIframe(newLocation);

    // const currentRoute = router?.pathname;
    // if (
    //   !currentRoute.includes(routes.portfolio.index) &&
    //   !currentRoute.includes(routes.auth.index) &&
    //   currentRoute !== routes.wizards.quote &&
    //   currentRoute !== routes.products.index
    // ) {
    setTimeout(function() {
      router.push(`${routes.home}?c=${newCountryValue}`);
    }, 50);
    // }
    runClearCache('cart,claims,quotes,receipts,applications');
  };
  const yourCurrentLocationText = intl.formatMessage(messages.yourCurrentLocation);
  Cookies.setCookie(templateStorage, theme?.template, userSettingsExpireTimeHrs);

  if (view === displayFullText) {
    return (
      <Container>
        <FormControl className={classes.formControl} fullWidth variant="outlined">
          <InputLabel id="demo-simple-select-label">Location</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id={props.id}
            name="countryCode"
            value={countryCode}
            fullWidth
            onChange={event => {
              countryVerificationStorage.removeCountryVerification();
              handleChange(event.target.value);
            }}
            label="Location"
            ref={countrySelectorRef}
          >
            {allLocations &&
              allLocations.map(option => {
                return (
                  <MenuItem
                    key={option?.alpha2}
                    value={option?.alpha2}
                    selected={countryCode === option?.alpha2}
                  >
                    {`${option?.name} - ${option?.alpha2}`}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
        <CountryChecker ref={countrySelectorRef} countryChange={handleChange} />
      </Container>
    );
  }
  return (
    <span>
      <FormControl className={classnames(classes.root)}>
        <Tooltip title={`${yourCurrentLocationText} ${yourLocation?.name}`}>
          <Select
            ref={countrySelectorRef}
            labelId="select-country-label"
            id="standard-select-country"
            size="small"
            value={countryCode}
            onChange={event => {
              countryVerificationStorage.removeCountryVerification();
              handleChange(event.target.value);
            }}
            IconComponent={() => null}
            className={classnames(classes.countryDropdown)}
            classes={{
              icon: classes.hideIcon,
              select: classes.hideIconPadding,
            }}
            input={<Input disableUnderline />}
            inputProps={{
              classes: {
                root: classes.inputRoot,
              },
            }}
            MenuProps={{
              classes: {
                paper: classnames({
                  [classes.dropdownStyle]: true,
                  [scrollClasses.scrollbar]: !isSmall,
                }),
              },
            }}
            renderValue={alpha2 => {
              // // console.log('alpha2: ', alpha2);
              // const option = locations.payload.find(x => x.countryCode === value);
              const option = initialAllLocations?.find(x => x.alpha2 === alpha2);
              if (view === displayFlag) {
                // // console.log('option: ', option);
                return (
                  <div
                    style={{ backgroundColor: isDutch ? backgroundColorOfImage : null }}
                  >
                    <img
                      src={
                        option?.staticFlagLink
                          ? option?.staticFlagLink
                          : formatImageUrlFromStrapi(option?.flag)?.url
                      }
                      className={classes.flag}
                      alt="Logo"
                    />
                  </div>
                );
              }

              if (view === displayFlagButton) {
                // // console.log('option: ', option);
                return (
                  <Avatar
                    alt={option.alpha2}
                    src={
                      option.staticFlagLink
                        ? option?.staticFlagLink
                        : formatImageUrlFromStrapi(option?.flag)?.url
                    }
                    classes={{
                      root: classnames(classes.avatar),
                      img: classnames(classes.flagButton),
                    }}
                  />
                );
              }

              return (
                <span
                  className={classnames(classes.inlineBlock, {
                    [classes.reverseColor]: reverseColor,
                  })}
                >
                  {option?.name}
                </span>
              );
            }}
          >
            {allLocations.map(option => {
              return (
                <MenuItem
                  key={option?.alpha2}
                  value={option?.alpha2}
                  className={classes.menuItem}
                >
                  <>
                    {view === displayFlag && (
                      <>
                        <img
                          style={{ marginRight: 10 }}
                          src={
                            option.staticFlagLink
                              ? option?.staticFlagLink
                              : formatImageUrlFromStrapi(option?.flag)?.url
                          }
                          className={classes.flag}
                          alt="Logo"
                        />

                        <Tooltip title={option?.name}>
                          <span className={classes.countryName}>{option?.name}</span>
                        </Tooltip>
                      </>
                    )}
                    {view === displayFullText && (
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Tooltip title={option?.name}>
                            <span className={classes.countryName}>{option?.name}</span>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    )}
                    {view === displayFlagButton && (
                      <Grid container spacing={1}>
                        <Grid item xs={3}>
                          <Avatar
                            alt={option.alpha2}
                            src={
                              option.staticFlagLink
                                ? option?.staticFlagLink
                                : formatImageUrlFromStrapi(option?.flag)?.url
                            }
                            classes={{
                              root: classnames(classes.avatar, classes.avatarMenuItem),
                              img: classnames(classes.flagButton),
                            }}
                          />
                        </Grid>
                        <Grid item xs={9} align="left">
                          <Tooltip title={option?.name}>
                            <Typography className={classes.countryName}>
                              {option?.name}
                            </Typography>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    )}
                  </>
                </MenuItem>
              );
            })}
          </Select>
        </Tooltip>
      </FormControl>
      <CountryChecker myRef={countrySelectorRef} countryChange={handleChange} />
    </span>
  );
}

Location.propTypes = {
  view: PropTypes.oneOf([displayFlag, displayFullText, displayFlagButton]),
  reverseColor: PropTypes.bool,
};

Location.defaultProps = {
  view: displayFlag,
  reverseColor: false,
};

export default Location;

/*
           IconComponent={() => (
              <ArrowDropDownIcon
                onChange={event => {
                  countryVerificationStorage.removeCountryVerification();
                  handleChange(event.target.value);
                }}
                className={classnames(
                  { [classes.none]: view === displayFlag || view === displayFlagButton },
                  {
                    [classes.inlineBlock]:
                      view !== displayFlag && view !== displayFlagButton,
                  },
                  {
                    [classes.reverseColor]: reverseColor,
                  },
                )}
              />
            )}
            */

/*
  // console.log(locations);
  // console.log(locations.success);

  if (locations.length === 0) {
    return (
      <FormControl className={classes.formControl}>
        <Select
          labelId="select-country-label"
          id="standard-select-country"
          IconComponent={() => <ArrowDropDownIcon className={classes.none} />}
          size="small"
          value={country}
          onChange={handleChange}
          className={classes.countryDropdown}
          input={<Input disableUnderline />}
          inputProps={{
            classes: {
              root: classes.inputRoot,
            },
          }}
          MenuProps={{ classes: { paper: classes.dropdownStyle } }}
          renderValue={value => {
            const option = countries.find(x => x.value === value);
            return option.label;
          }}
        >
          {countries.map(option => (

          ))}
        </Select>
      </FormControl>
    );
  }
  */
